import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSeo, organizationSchema, breadcrumbSchema } from "../lib/useSeo";
import { CTASection } from "../components/sections/CTASection";
import { PROJECTS } from "../lib/projects";

export default function PortfolioPage() {
  const { t, locale } = useI18n();
  const isDE = locale === "de";

  useSeo({
    title: isDE
      ? "Projekte — Referenzen & Case Studies | Joshi FusionTech Media"
      : "Portfolio — Projects & Case Studies | Joshi FusionTech Media",
    description: isDE
      ? "Ausgewählte Projekte: KI-Automatisierung, SaaS-Plattformen, Webentwicklung, Branding und SEO für Startups und Unternehmen aus Berlin."
      : "Selected projects: AI automation, SaaS platforms, web development, branding and SEO for startups and businesses, from Berlin.",
    canonical: "https://www.joshifusiontech.de/portfolio",
    locale,
    jsonLd: [
      organizationSchema(),
      breadcrumbSchema([
        { name: isDE ? "Start" : "Home", url: "https://www.joshifusiontech.de/" },
        { name: isDE ? "Projekte" : "Portfolio", url: "https://www.joshifusiontech.de/portfolio" },
      ]),
    ],
  });

  return (
    <main data-testid="portfolio-page" className="pt-32">
      <section className="px-6 md:px-12 lg:px-24 pb-16">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-brand-azure" />
            <span className="label-mono">{t.portfolio.kicker}</span>
          </div>
          <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl tracking-tighter text-white">
            {t.portfolio.title}
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">{t.portfolio.subtitle}</p>
          <p className="mt-3 text-sm font-mono uppercase tracking-[0.22em] text-white/40">
            {isDE
              ? "Showcase-Inhalte · ersetzbar durch reales Material auf Anfrage"
              : "Showcase content · replaceable with real material on request"}
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROJECTS.map((p, i) => (
            <Link
              key={p.slug}
              to={`/projects/${p.slug}`}
              data-testid={`portfolio-case-${i}`}
              className="group glass rounded-3xl overflow-hidden hover:border-brand-orange/40 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(255,106,0,0.18)] transition-all duration-300 flex flex-col"
            >
              <div className="relative h-52 md:h-56 overflow-hidden">
                <img
                  src={p.assets.hero}
                  alt={p.title[locale]}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-brand-orange/90 text-white text-[10px] font-mono uppercase tracking-[0.22em] shadow-lg">
                  {p.industry[locale]}
                </div>
                <div className="absolute bottom-3 right-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/75">
                  Case study
                </div>
              </div>
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <h3 className="font-display font-semibold text-lg md:text-xl text-white tracking-tight group-hover:text-brand-orange transition-colors leading-snug">
                  {p.title[locale]}
                </h3>
                <p className="mt-3 text-sm text-white/65 line-clamp-3 flex-1 leading-relaxed">
                  {p.summary[locale]}
                </p>
                <div className="mt-6 pt-5 border-t border-white/[0.06] flex items-center justify-between">
                  <span className="label-mono text-brand-orange/90">
                    {isDE ? "Case Study ansehen" : "View case study"}
                  </span>
                  <span className="w-9 h-9 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center text-brand-orange group-hover:bg-brand-orange group-hover:text-white transition-all">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CTASection />
    </main>
  );
}
